import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PriceId } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import type {
  Cart,
  EmptyCart,
  MaterializedMenuProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  getMaxCartQuantity,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

import { useCreateCart, useUpdateCartProduct } from '../../../data-access';
import { useGetMaterializedMenuProduct } from '../../../data-access/lib/queries/product';
import { useGetProductPrices } from '../useGetProductPrices/useGetProductPrices';

export interface AddProductToBagParams {
  cart?: Cart | EmptyCart;
  onSuccess: (newCart?: Cart) => void;
  priceId?: PriceId;
  product: AlgoliaProduct | MaterializedMenuProduct;
  store: Store;
}

export const useAddProductToBag = ({
  cart,
  product: initialProduct,
  onSuccess,
  priceId,
  store,
}: AddProductToBagParams) => {
  const storeId = store.id;
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);

  const { data: product } = useGetMaterializedMenuProduct(
    {
      productId: initialProduct.product_id,
      storeId: storeId,
    },
    { initialData: initialProduct }
  );

  const getProductPrice = useGetProductPrices(product, specials);
  const weights = availableWeightsForMenuProduct(product);

  const { priceId: selectedWeight } = getProductPrice(
    priceId || lowestPriceForMenuProduct(product, weights) || 'each'
  );

  const { mutate: createCart, isLoading: createLoading } = useCreateCart({
    onSuccess,
  });
  const { mutate: updateCartProduct, isLoading: updateLoading } =
    useUpdateCartProduct({ onSuccess });
  const { mutate: addCartProduct, isLoading: addLoading } = useAddCartProduct({
    onSuccess,
  });

  const onAddToBag = useCallback(
    async ({ quantity = 1, weight = selectedWeight }) => {
      const existingCartProduct = (cart?.products ?? []).find(
        ({ price_id, product_id }) =>
          product.product_id === product_id && price_id === weight
      );

      if (
        existingCartProduct &&
        existingCartProduct.count ===
          getMaxCartQuantity(existingCartProduct, existingCartProduct.price_id)
      ) {
        return;
      }
      if (!cart?.uuid) {
        createCart({
          products: [
            {
              count: quantity,
              price_id: weight,
              product_id: product.product_id,
            },
          ],
          store_id: storeId,
        });
      } else if (existingCartProduct) {
        updateCartProduct({
          cart_uuid: cart.uuid,
          product: {
            count: existingCartProduct.count + quantity,
            price_id: weight,
            product_id: product.product_id,
          },
        });
      } else {
        addCartProduct({
          app_mode: 'kiosk',
          app_mode_name: 'kiosk',
          cart_uuid: cart.uuid,
          product: {
            count: quantity,
            price_id: weight,
            product_id: product.product_id,
            recommended: false,
          },
          store_id: storeId,
          user_agent: window.navigator.userAgent,
        });
      }
    },
    [
      addCartProduct,
      cart?.products,
      cart?.uuid,
      createCart,
      product.product_id,
      selectedWeight,
      storeId,
      updateCartProduct,
    ]
  );
  return {
    isLoading: createLoading || updateLoading || addLoading,
    onAddToBag,
  };
};
